import React, { type FC, useCallback, useMemo } from 'react'

import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import {
  ACTION_BUTTON_TYPE_ACTIONS,
  ButtonAction,
  Checkbox,
  DataListExtraColumn,
  DataListHeader,
  DataListRow,
  GRID_VARIANT_SMALL,
} from 'mailup-web-components'

import ItemActions from 'Pages/GroupsList/components/ItemActions'
import ResultItem from 'Pages/GroupsList/components/ResultItem'
import ResultItemSkel from 'Pages/GroupsList/components/ResultItemSkel'
import type { PaginationState } from 'Pages/GroupsList/model'
import {
  fetchResultsCallback,
  toggleSelectionCallback,
} from 'Pages/GroupsList/recoil/callbacks/callbacks'
import { panelEditOpenCallback } from 'Pages/GroupsList/recoil/callbacks/edit'
import {
  modalCopyOpenCallback,
  modalDeleteOpenCallback,
} from 'Pages/GroupsList/recoil/callbacks/modals'
import {
  paginationState,
  resultsState,
  selectedAllState,
} from 'Pages/GroupsList/recoil/state/state'

const EXTRA_COLUMN_AFTER_WIDTH = '28px'

const ResultsTable: FC = () => {
  const intl = useIntl()
  const navigate = useNavigate()

  const items = useRecoilValue(resultsState)
  const { orderBy, orderDescending, loading, pageSize } =
    useRecoilValue(paginationState)
  const selectedAll = useRecoilValue(selectedAllState)

  const fetchResults = useRecoilCallback(fetchResultsCallback, [])
  const toggleSelection = useRecoilCallback(toggleSelectionCallback, [])
  const modalDeleteOpen = useRecoilCallback(modalDeleteOpenCallback, [])

  const panelEditOpen = useRecoilCallback(panelEditOpenCallback, [])
  const modalCopyOpen = useRecoilCallback(modalCopyOpenCallback, [])

  const handleChangeColumnsOrder = (
    fieldName: PaginationState['orderBy'],
  ): void => {
    void fetchResults({ orderBy: fieldName }, {})
  }

  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const heads: any[] = useMemo(() => {
    return [
      {
        label: (
          <Checkbox
            checked={selectedAll}
            data-mu-test="select-all"
            onChange={toggleSelection}
          />
        ),
        span: 1,
      },
      {
        label: intl.formatMessage({ id: 'Load.MailupCommon.Id' }),
        span: 2,
        sortable: true,
        field: 'Id',
      },
      {
        label: intl.formatMessage({ id: 'Load.GroupsList.FieldNameNotes' }),
        span: 5,
        sortable: true,
        field: 'Name',
      },
      {
        label: intl.formatMessage({ id: 'Load.MailupCommon.Type' }),
        span: 3,
        sortable: true,
        field: 'GroupType',
      },
      {
        label: intl.formatMessage({ id: 'Load.GroupsList.EmailSubscribed' }),
        span: 3,
        sortable: true,
        field: 'EmailSubscribed',
      },
      {
        label: intl.formatMessage({ id: 'Load.GroupsList.EmailUnsubscribed' }),
        span: 3,
        sortable: true,
        field: 'EmailUnsubscribed',
      },
      {
        label: intl.formatMessage({ id: 'Load.GroupsList.EmailPending' }),
        span: 3,
        sortable: true,
        field: 'EmailPending',
      },
      {
        label: intl.formatMessage({
          id: 'Load.GroupsList.SMSSubscribed',
        }),
        span: 3,
        sortable: true,
        field: 'SmsSubscribed',
      },
      {
        label: intl.formatMessage({ id: 'Load.GroupsList.SMSUnsubscribed' }),
        span: 3,
        sortable: true,
        field: 'SmsUnsubscribed',
      },
    ]
  }, [intl, selectedAll, toggleSelection])

  const spans: number[] = heads.map((o) => o.span)

  const cols: number = heads.reduce<number>(
    (acc, o) => acc + (o.span as number),
    0,
  )

  const handleEdit = useCallback(
    (idGroup: number) => () => {
      void panelEditOpen(idGroup)
    },
    [panelEditOpen],
  )

  const handleEmailSubscribers = useCallback(
    (idGroup: number) => {
      navigate(`/recipients/list?sch=email&ssst=optin&sig=${idGroup}`)
    },
    [navigate],
  )

  const handleSMSSubscribers = useCallback(
    (idGroup: number) => {
      navigate(`/recipients/list?sch=sms&ssst=optin&sig=${idGroup}`)
    },
    [navigate],
  )

  return (
    <>
      <DataListHeader
        heads={heads}
        afterWidth={EXTRA_COLUMN_AFTER_WIDTH}
        orderByStatus={orderBy}
        orderDescendingStatus={orderDescending}
        handleChangeColumnsOrderCallback={handleChangeColumnsOrder}
        gridVariant={GRID_VARIANT_SMALL}
        cols={cols}
      />
      <section>
        {loading &&
          Array.from({
            length: items.length > 0 ? items.length : pageSize,
          }).map((_, idx) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
            <DataListRow key={idx}>
              <ResultItemSkel cols={cols} spans={spans} />
              <DataListExtraColumn after width={EXTRA_COLUMN_AFTER_WIDTH}>
                <ButtonAction type={ACTION_BUTTON_TYPE_ACTIONS} disabled />
              </DataListExtraColumn>
            </DataListRow>
          ))}

        {!loading &&
          items.map((item) => (
            <DataListRow key={item.Id}>
              <ResultItem
                cols={cols}
                spans={spans}
                item={item}
                onEdit={handleEdit(item.Id)}
              />
              <DataListExtraColumn after width={EXTRA_COLUMN_AFTER_WIDTH}>
                <ItemActions
                  item={item}
                  onEmailSubscribers={handleEmailSubscribers}
                  onSMSSubscribers={handleSMSSubscribers}
                  onGroupEdit={panelEditOpen}
                  onGroupDelete={modalDeleteOpen}
                  onCopyToList={modalCopyOpen}
                />
              </DataListExtraColumn>
            </DataListRow>
          ))}
      </section>
    </>
  )
}

export default ResultsTable
